import React, { useState, useEffect } from 'react';

// UI e Componentes
import Sidebar from './components/Sidebar';
import { Toast } from './components/Toast';

// Telas
import TelaLogin from './screens/TelaLogin';
import Dashboard from './screens/Dashboard';
import Procedimentos from './screens/Procedimentos';
import MapaUnidades from './screens/MapaUnidades';
import AdminProcedimentos from './screens/AdminProcedimentos';
import MapaRede from './screens/MapaRede';
import AdminUsuarios from './screens/AdminUsuarios';
import AdminEscolas from './screens/AdminEscolas'; // <-- NOVO

import { Menu as MenuIcon, Sun, Moon } from 'lucide-react';

function App() {
  const [autenticado, setAutenticado] = useState(false);
  const [user, setUser] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('dashboard');
  const [sidebarAberta, setSidebarAberta] = useState(true);
  const [toast, setToast] = useState(null);
  const [escolaPreSelecionada, setEscolaPreSelecionada] = useState(null);
  const [tema, setTema] = useState(localStorage.getItem('tema_ctc') || 'dark');

  useEffect(() => {
    const usuarioLogado = localStorage.getItem('usuario_ctc');
    if (usuarioLogado) {
      const parsedUser = JSON.parse(usuarioLogado);
      setAutenticado(true);
      setUser(parsedUser);
    }

    const path = window.location.pathname;
    if (path.includes('/escola/')) {
      const cie = path.split('/').pop();
      setEscolaPreSelecionada(cie);
      setAbaAtiva('dashboard');
    }
  }, []);

  // 🔴 Verificação periódica de força de logout (a cada 5 segundos)
  useEffect(() => {
    if (!autenticado || !user?.id) return;

    const intervalo = setInterval(async () => {
      try {
        const resposta = await fetch(`/api/usuarios/${user.id}/check-session`);
        const dados = await resposta.json();

        if (dados.logout) {
          localStorage.removeItem('usuario_ctc');
          setAutenticado(false);
          setUser(null);
        }
      } catch (erro) {
        console.error("Erro ao checar sessão:", erro);
      }
    }, 5000);

    return () => clearInterval(intervalo);
  }, [autenticado, user]);

  useEffect(() => {
    localStorage.setItem('tema_ctc', tema);
  }, [tema]);

  const showToast = (mensagem, tipo = 'success') => {
    setToast({ mensagem, tipo });
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copiado!`, 'success');
  };

  const handleLogout = () => {
    localStorage.removeItem('usuario_ctc');
    setAutenticado(false);
    setUser(null);
  };

  const toggleTema = () => {
    setTema(tema === 'dark' ? 'light' : 'dark');
  };

  const mudarAba = (novaAba) => {
    // Hierarquia de Permissões
    const abasSuperAdmin = ['admin_usuarios', 'admin_escolas']; 
    const abasAdmin = ['admin_procedimentos']; 

    // Bloqueio SuperAdmin
    if (abasSuperAdmin.includes(novaAba) && user?.perfil !== 'superadmin') {
      showToast("Acesso restrito ao SuperAdmin", "error");
      return;
    }

    // Bloqueio Admin/SuperAdmin
    if (abasAdmin.includes(novaAba) && !['admin', 'superadmin'].includes(user?.perfil)) {
      showToast("Acesso restrito a administradores", "error");
      return;
    }

    setAbaAtiva(novaAba);
    if (novaAba !== 'dashboard') setEscolaPreSelecionada(null);
  };

  if (!autenticado) return <TelaLogin onLogin={(val) => {
    const usuarioLogado = localStorage.getItem('usuario_ctc');
    if (usuarioLogado) setUser(JSON.parse(usuarioLogado));
    setAutenticado(val);
  }} />;

  const renderTela = () => {
    switch (abaAtiva) {
      case 'dashboard':
        return <Dashboard showToast={showToast} tema={tema} cieInicial={escolaPreSelecionada} userPerfil={user?.perfil} />;
      case 'mapa':
        return <MapaUnidades showToast={showToast} tema={tema} />;
      case 'procedimentos':
        return <Procedimentos copy={copyToClipboard} tema={tema} />;
      case 'rede':
        return <MapaRede showToast={showToast} tema={tema} />;
      case 'admin_procedimentos':
        return ['admin', 'superadmin'].includes(user?.perfil) ? 
          <AdminProcedimentos showToast={showToast} tema={tema} /> : 
          <Dashboard showToast={showToast} tema={tema} />;
      case 'admin_usuarios': 
        return user?.perfil === 'superadmin' ? 
          <AdminUsuarios showToast={showToast} tema={tema} /> : 
          <Dashboard showToast={showToast} tema={tema} />;
      case 'admin_escolas': // <-- NOVO
        return user?.perfil === 'superadmin' ? 
          <AdminEscolas showToast={showToast} tema={tema} /> : 
          <Dashboard showToast={showToast} tema={tema} />;
      default:
        return <Dashboard showToast={showToast} tema={tema} />;
    }
  };

  return (
    <div className={`${tema} min-h-screen transition-colors duration-300`}>
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-200 font-sans overflow-x-hidden transition-colors duration-300">
        {toast && <Toast mensagem={toast.mensagem} tipo={toast.tipo} onClose={() => setToast(null)} />}
        
        <Sidebar 
          user={user} 
          abaAtiva={abaAtiva} 
          setAbaAtiva={mudarAba} 
          sidebarAberta={sidebarAberta}
          setSidebarAberta={setSidebarAberta}
          onLogout={handleLogout}
        />

        <main className="flex-1 min-w-0 flex flex-col">
          <header className="sticky top-0 z-[100] w-full bg-white/80 dark:bg-slate-950/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 p-4">
            <div className="flex items-center justify-between max-w-5xl mx-auto w-full">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setSidebarAberta(true)} 
                  className={`p-3 bg-white dark:bg-slate-900 rounded-2xl text-emerald-600 dark:text-emerald-400 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 lg:hidden ${sidebarAberta ? 'hidden' : 'flex'}`}
                >
                  <MenuIcon size={24}/>
                </button>
                <div className="flex flex-col justify-center select-none">
                  <span className="font-black text-emerald-600 dark:text-emerald-400 tracking-tighter leading-none text-xl">CTC-IT</span>
                  <span className="text-[8px] text-slate-400 dark:text-slate-500 font-black uppercase tracking-[0.2em] mt-1">Gerenciamento de Ativos</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user?.perfil === 'superadmin' && (
                  <span className="hidden md:block text-[9px] font-black bg-emerald-500/10 text-emerald-500 px-3 py-1 rounded-full uppercase tracking-widest border border-emerald-500/20">
                    Modo Super
                  </span>
                )}
                <button onClick={toggleTema} className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:scale-105 transition-all">
                  {tema === 'dark' ? <Sun size={20} className="text-yellow-500" /> : <Moon size={20} className="text-indigo-600" />}
                </button>
              </div>
            </div>
          </header>

          <div className="flex-1 p-4 lg:p-10">
            <section className="max-w-5xl mx-auto w-full">
              {renderTela()}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;